import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAudits } from 'src/store/audits/useAudits';
import { DEFAULT_LANGUAGE, AuditStatus } from 'src/contstant/commonLabels';
import { GetAuditSummaryResponse, Audit } from 'src/types/helios/Audit';
import { apiClient } from 'src/data/api/apiClient';
import { ServiceType, API_ENDPOINTS, createApiUrl } from 'src/contstant/apiEndpoints';
import { areDatesEqual } from 'src/utils/dateUtils';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';

// Constants for API pagination
const DEFAULT_POLLING_INTERVAL = 10000; // 10 seconds - balance between responsiveness and API load
const MAX_QUESTIONS = 500; // Fetch all questions (API validation error at 600)

interface UseAuditSummaryProps {
  businessUnitId: string;
  auditId: string;
  pollingInterval?: number;
  additionalLanguages?: string[];
}

/**
 * Custom hook for managing audit summary data with automatic sync detection
 * Only updates Redux state when audit data from DDB and OpenSearch are synchronized
 * Uses polling to detect when data sources are in sync
 */
export const useAuditSummary = ({
  businessUnitId,
  auditId,
  pollingInterval = DEFAULT_POLLING_INTERVAL,
  additionalLanguages = [],
}: UseAuditSummaryProps) => {
  // Get Redux actions and state
  const {
    getAuditSummary,
    getAuditById,
    auditSummary: reduxAuditSummary,
    currentAudit: reduxCurrentAudit,
    auditSummaryApiStatus,
  } = useAudits();
  const { enableEnhancedResponseTypes } = useFeatureFlags();

  // Refs to store data and prevent unnecessary re-renders
  const auditSummaryRef = useRef<GetAuditSummaryResponse | undefined>(undefined);
  const currentAuditRef = useRef<Audit | undefined>(undefined);
  const lastAuditIdRef = useRef<string | null>(null);
  const lastBusinessUnitIdRef = useRef<string | null>(null);
  const initialLoadCompletedRef = useRef<boolean>(false);
  const loadDataCalledRef = useRef<boolean>(false); // Track if loadData has been called

  // Local state for UI updates only
  const [statusesMatch, setStatusesMatch] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [localAuditSummary, setLocalAuditSummary] = useState<
    GetAuditSummaryResponse | undefined
  >(undefined);
  const [localCurrentAudit, setLocalCurrentAudit] = useState<Audit | undefined>(
    undefined,
  );
  const [dataLoaded, setDataLoaded] = useState(false); // Track when refs are populated

  // Refs for polling control
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const isCheckingStatusRef = useRef(false); // Prevent concurrent status checks

  // Display audit summary - use local state if available, otherwise use Redux state
  const displayAuditSummary = useMemo(() => {
    return localAuditSummary || reduxAuditSummary || undefined;
  }, [localAuditSummary, reduxAuditSummary]);

  const currentAudit = useMemo(() => {
    return localCurrentAudit || reduxCurrentAudit || undefined;
  }, [localCurrentAudit, reduxCurrentAudit]);

  /**
   * Generic API caller that handles errors and fallback logic
   * Separated from URL/payload construction for better separation of concerns
   */
  const callApi = useCallback(
    async <TResponse>(
      apiCall: () => Promise<TResponse>,
      fallbackData: TResponse | undefined,
      errorContext: string,
    ): Promise<TResponse | null> => {
      try {
        return await apiCall();
      } catch (error) {
        console.error(`Error ${errorContext}:`, error);

        // Fallback to provided data if API call fails
        if (fallbackData) {
          return fallbackData;
        }

        // Set error message for UI
        setErrorMessage(
          `Failed to ${errorContext}. Please try again. (${error instanceof Error ? error.message : 'Unknown error'})`,
        );
        return null;
      }
    },
    [],
  );

  /**
   * Fetches audit data from DynamoDB
   * Updates ref but NOT local state during polling
   */
  const fetchAuditById = useCallback(async (): Promise<Audit | null> => {
    const result = await callApi(
      async () => {
        const data = await apiClient.get<{ audit: Audit }>(
          ServiceType.HELIOS,
          createApiUrl(API_ENDPOINTS.HELIOS_SERVICE.AUDITS.GET(businessUnitId, auditId)),
        );
        return data.audit;
      },
      reduxCurrentAudit,
      `fetch audit ${auditId}`,
    );

    if (result) {
      currentAuditRef.current = result;
    }

    return result;
  }, [businessUnitId, auditId, reduxCurrentAudit, callApi]);

  /**
   * Fetches audit summary with all questions from OpenSearch
   * Updates ref but NOT local state during polling
   */
  const fetchAuditSummary =
    useCallback(async (): Promise<GetAuditSummaryResponse | null> => {
      const result = await callApi(
        async () => {
          return await apiClient.post<GetAuditSummaryResponse, any>(
            ServiceType.HELIOS,
            createApiUrl(
              API_ENDPOINTS.HELIOS_SERVICE.AUDITS.SUMMARY(businessUnitId, auditId),
            ),
            {
              language: DEFAULT_LANGUAGE,
              maxResults: MAX_QUESTIONS, // Fetch all questions
              ...(additionalLanguages.length > 0 && { additionalLanguages }),
            },
          );
        },
        reduxAuditSummary,
        `fetch audit summary for ${auditId}`,
      );

      if (result) {
        auditSummaryRef.current = result;
      }

      return result;
    }, [businessUnitId, auditId, additionalLanguages, reduxAuditSummary, callApi]);

  /**
   * Fetches all question responses from DynamoDB for comparison
   */
  const fetchQuestionResponsesFromDDB = useCallback(async () => {
    try {
      const response = await apiClient.get<any>(
        ServiceType.HELIOS,
        createApiUrl(
          API_ENDPOINTS.HELIOS_SERVICE.QUESTION_RESPONSES.LIST(businessUnitId, auditId),
        ),
        {
          language: DEFAULT_LANGUAGE,
          ...(additionalLanguages.length > 0 && { additionalLanguages }),
        },
      );
      return response.auditQuestionResponses || [];
    } catch (error) {
      console.error('Error fetching question responses from DDB:', error);
      return null;
    }
  }, [businessUnitId, auditId, additionalLanguages]);

  /**
   * Compares main question response between DDB and OpenSearch
   * Returns object with match status and response counts
   */
  const compareMainQuestionResponse = useCallback(
    (
      ddbQuestion: any,
      osQuestion: any,
    ): { matches: boolean; ddbHasResponse: boolean; osHasResponse: boolean } => {
      const ddbMainResponseResult = ddbQuestion.responseResult;
      const osMainResponseResult =
        osQuestion.responseDetails?.mainQuestionResponse?.responseResult;

      const ddbMainRespondedAt = ddbQuestion.respondedAt;
      const osMainRespondedAt =
        osQuestion.responseDetails?.mainQuestionResponse?.respondedAt;

      // Check if DDB has a response but OpenSearch doesn't
      if (ddbMainResponseResult && !osMainResponseResult) {
        return {
          matches: false,
          ddbHasResponse: !!ddbMainResponseResult,
          osHasResponse: !!osMainResponseResult,
        };
      }

      // Compare response results
      if (ddbMainResponseResult !== osMainResponseResult) {
        return {
          matches: false,
          ddbHasResponse: !!ddbMainResponseResult,
          osHasResponse: !!osMainResponseResult,
        };
      }

      // Compare response dates
      if (!areDatesEqual(ddbMainRespondedAt, osMainRespondedAt)) {
        return {
          matches: false,
          ddbHasResponse: !!ddbMainResponseResult,
          osHasResponse: !!osMainResponseResult,
        };
      }

      return {
        matches: true,
        ddbHasResponse: !!ddbMainResponseResult,
        osHasResponse: !!osMainResponseResult,
      };
    },
    [],
  );

  /**
   * Compares sub-question responses between DDB and OpenSearch
   * Returns true if all sub-responses match
   */
  const compareSubQuestionResponses = useCallback(
    (ddbSubResponses: any[], osSubResponses: any[]): boolean => {
      // Check if lengths match
      if (ddbSubResponses.length !== osSubResponses.length) {
        return false;
      }

      // Create a map of OpenSearch sub-responses by subQuestionId for quick lookup
      const osSubResponsesMap = new Map<string, any>(
        osSubResponses.map((sr: any) => [sr.subQuestionId, sr]),
      );

      // Compare each sub-response
      for (const ddbSubResponse of ddbSubResponses) {
        const osSubResponse = osSubResponsesMap.get(ddbSubResponse.subQuestionId);

        if (!osSubResponse) {
          return false;
        }

        // Compare response results
        if (ddbSubResponse.result !== osSubResponse.response?.responseResult) {
          return false;
        }

        // Compare response dates
        if (
          !areDatesEqual(ddbSubResponse.respondedAt, osSubResponse.response?.respondedAt)
        ) {
          return false;
        }
      }

      return true;
    },
    [],
  );

  /**
   * Compares question responses between DynamoDB and OpenSearch
   * Returns true if all responses match
   */
  const compareQuestionResponses = useCallback(
    (ddbQuestions: any[], osQuestions: any[]) => {
      // If lengths don't match, data is not synced
      if (ddbQuestions.length !== osQuestions.length) {
        return false;
      }

      // Count how many questions have responses in DDB vs OpenSearch
      let ddbResponseCount = 0;
      let osResponseCount = 0;

      // Create a map of OpenSearch questions by questionId for quick lookup
      const osQuestionsMap = new Map(
        osQuestions.map((q) => [q.questionDetails?.questionId, q]),
      );

      // Check each DDB question against OpenSearch
      for (const ddbQuestion of ddbQuestions) {
        const osQuestion = osQuestionsMap.get(ddbQuestion.questionId);

        if (!osQuestion) {
          return false; // Question not found in OpenSearch
        }

        // Compare main question response
        const mainResponseComparison = compareMainQuestionResponse(
          ddbQuestion,
          osQuestion,
        );

        if (!mainResponseComparison.matches) {
          return false;
        }

        // Update response counts
        if (mainResponseComparison.ddbHasResponse) {
          ddbResponseCount++;
        }
        if (mainResponseComparison.osHasResponse) {
          osResponseCount++;
        }

        // Compare sub-question responses if they exist
        const ddbSubResponses = ddbQuestion.subResponses || [];
        const osSubResponses = osQuestion.responseDetails?.subQuestionResponses || [];

        // ERT conditional question handling:
        // - DDB (ListAuditQuestionResponses) returns ALL sub-responses (both branches)
        //   because perform-audit needs them all to avoid data loss on updates.
        // - OpenSearch (GetAuditSummary) only stores TRIGGERED sub-responses
        //   (filtered by Lambda during DDB stream sync based on parent result).
        // - Without filtering, DDB count > OS count → comparison always fails
        //   → page stuck on "Synchronizing Audit Data" forever.
        // Fix: Filter DDB subs to only the triggered branch before comparing,
        //   matching what OpenSearch is expected to have.
        const parentResult = ddbQuestion.responseResult;
        const isConditional =
          enableEnhancedResponseTypes && ddbQuestion.hasConditionalSubQuestions;
        const filteredDdbSubResponses =
          isConditional && parentResult && parentResult !== 'NA'
            ? ddbSubResponses.filter(
                (sub: any) =>
                  sub.showWhenParentIs === parentResult ||
                  sub.showWhenParentIs === 'BOTH',
              )
            : isConditional
              ? [] // Parent not answered or NA → OS has no conditional subs
              : ddbSubResponses; // Non-conditional or flag OFF → compare all (unchanged)

        if (!compareSubQuestionResponses(filteredDdbSubResponses, osSubResponses)) {
          return false;
        }
      }

      // Final check: ensure response counts match
      if (ddbResponseCount !== osResponseCount) {
        return false;
      }

      return true; // All responses match
    },
    [compareMainQuestionResponse, compareSubQuestionResponses],
  );

  // Check if statuses match - enhanced logic for COMPLETED and IN_PROGRESS audits
  const checkStatusesMatch = useCallback(async () => {
    // Prevent concurrent status checks
    if (isCheckingStatusRef.current) {
      return statusesMatch; // Return current state
    }

    isCheckingStatusRef.current = true;

    try {
      const auditData = currentAuditRef.current;
      const summaryData = auditSummaryRef.current;

      // If either data is missing, we can't determine if statuses match
      if (!auditData || !summaryData?.audit) {
        return false;
      }

      // First check if audit status matches
      const statusMatches = auditData.status === summaryData.audit.status;

      if (!statusMatches) {
        setStatusesMatch(false);
        return false;
      }

      // For COMPLETED and IN_PROGRESS audits, also check if question responses match
      if (
        auditData.status === AuditStatus.COMPLETED ||
        auditData.status === AuditStatus.IN_PROGRESS
      ) {
        // Fetch all question responses from DynamoDB
        const ddbQuestions = await fetchQuestionResponsesFromDDB();

        if (!ddbQuestions) {
          // If we can't fetch DDB questions, assume not synced
          setStatusesMatch(false);
          return false;
        }

        // Always fetch fresh data from OpenSearch to detect new changes
        try {
          const fullSummary = await apiClient.post<GetAuditSummaryResponse, any>(
            ServiceType.HELIOS,
            createApiUrl(
              API_ENDPOINTS.HELIOS_SERVICE.AUDITS.SUMMARY(businessUnitId, auditId),
            ),
            {
              language: DEFAULT_LANGUAGE,
              maxResults: MAX_QUESTIONS, // Fetch all questions
              ...(additionalLanguages.length > 0 && { additionalLanguages }),
            },
          );

          const osQuestions = fullSummary.questions || [];

          // Compare all question responses
          const responsesMatch = compareQuestionResponses(ddbQuestions, osQuestions);

          // ONLY update local state when data is fully synced
          if (responsesMatch) {
            // Data is synced - update local state to show complete OpenSearch data
            setLocalAuditSummary(fullSummary);
            setLocalCurrentAudit(auditData);
          }
          // If NOT synced, don't update local state - keep showing initial Redux data

          setStatusesMatch(responsesMatch);
          return responsesMatch;
        } catch (error) {
          console.error('Error fetching full OpenSearch questions:', error);
          setStatusesMatch(false);
          return false;
        }
      }

      // For other statuses (SCHEDULED, NOT_STARTED, EXPIRED), just check status
      setStatusesMatch(statusMatches);
      return statusMatches;
    } finally {
      isCheckingStatusRef.current = false;
    }
  }, [
    fetchQuestionResponsesFromDDB,
    compareQuestionResponses,
    businessUnitId,
    auditId,
    additionalLanguages,
  ]);

  // Function to start polling
  const startPolling = useCallback(() => {
    // Clear any existing interval
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    // Set up new polling interval
    pollingRef.current = setInterval(async () => {
      // Only poll if statuses don't match
      if (isPollingRef.current) {
        try {
          // Fetch current audit data and summary, then check if they match
          const auditData = await fetchAuditById();
          const summaryData = await fetchAuditSummary();

          // If we have both pieces of data, check if statuses match
          if (auditData && summaryData?.audit) {
            const doStatusesMatch = await checkStatusesMatch();

            if (doStatusesMatch) {
              // Stop polling
              isPollingRef.current = false;
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
            }
          }
        } catch (error) {
          // Improved error handling with more context
          console.error('Error during polling cycle:', error);
          setErrorMessage(
            `Polling error: ${error instanceof Error ? error.message : 'Unknown error'}. Will retry automatically.`,
          );

          // Don't stop polling on error - it will retry on next interval
        }
      } else {
        // Stop polling if we're not supposed to be polling anymore
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    }, pollingInterval);
  }, [pollingInterval, fetchAuditById, fetchAuditSummary, checkStatusesMatch]);

  // Initial data load - use Redux actions for first load
  // Using a ref-based approach to prevent dependency issues
  const loadDataRef = useRef<() => void>();

  loadDataRef.current = () => {
    // Prevent duplicate calls for the same audit
    // Check if we've already started loading this exact audit
    if (
      loadDataCalledRef.current &&
      lastAuditIdRef.current === auditId &&
      lastBusinessUnitIdRef.current === businessUnitId
    ) {
      return;
    }

    // Mark as called and set IDs immediately to prevent race conditions
    loadDataCalledRef.current = true;
    lastAuditIdRef.current = auditId;
    lastBusinessUnitIdRef.current = businessUnitId;

    // Reset state
    setIsLoading(true);
    setErrorMessage(null);
    isPollingRef.current = false;
    initialLoadCompletedRef.current = false;

    // Stop any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    // Use Redux actions for initial load - fetch all questions
    Promise.all([
      getAuditSummary({
        businessUnitId,
        auditId,
        language: DEFAULT_LANGUAGE,
        additionalLanguages,
        maxResults: MAX_QUESTIONS, // Fetch all questions
      }),

      getAuditById({
        businessUnitId,
        auditId,
      }),
    ])
      .then(async () => {
        // Wait a tick for Redux state to update
        await new Promise((resolve) => setTimeout(resolve, 0));

        initialLoadCompletedRef.current = true;

        // Trigger state update - this will cause the check statuses effect to run
        setDataLoaded(true);

        // IDs already set at the start of loadData to prevent race conditions
      })
      .catch((error) => {
        // Improved error handling with more context
        console.error(`Error loading initial data for audit ${auditId}:`, error);

        // More descriptive error message with error details
        const errorDetails = error instanceof Error ? error.message : 'Unknown error';
        setErrorMessage(
          `Failed to load audit data: ${errorDetails}. Please try refreshing the page.`,
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loadData = useCallback(() => {
    loadDataRef.current?.();
  }, []);

  // Update refs when Redux state changes (always keep refs in sync)
  useEffect(() => {
    if (reduxCurrentAudit) {
      currentAuditRef.current = reduxCurrentAudit;

      // Only update local state during initial load (not during polling)
      if (initialLoadCompletedRef.current && !isPollingRef.current) {
        setLocalCurrentAudit(reduxCurrentAudit);
      }
    }
  }, [reduxCurrentAudit]);

  useEffect(() => {
    if (reduxAuditSummary) {
      auditSummaryRef.current = reduxAuditSummary;

      // Only update local state during initial load (not during polling)
      if (initialLoadCompletedRef.current && !isPollingRef.current) {
        setLocalAuditSummary(reduxAuditSummary);
      }
    }
  }, [reduxAuditSummary]);

  // Check statuses ONCE after both pieces of data are loaded
  // Use a ref to track if we've already done the initial check
  const initialCheckDoneRef = useRef(false);

  useEffect(() => {
    if (
      initialLoadCompletedRef.current &&
      !isPollingRef.current &&
      currentAuditRef.current &&
      auditSummaryRef.current &&
      !initialCheckDoneRef.current
    ) {
      initialCheckDoneRef.current = true; // Mark as done BEFORE calling to prevent re-entry
      checkStatusesMatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, reduxCurrentAudit, reduxAuditSummary]); // Depend on Redux state updates, not refs

  // Start polling if statuses don't match
  useEffect(() => {
    if (!initialLoadCompletedRef.current) {
      return;
    }

    if (!statusesMatch && !isPollingRef.current) {
      // Statuses don't match and we're not polling - start polling
      isPollingRef.current = true;
      startPolling();
    } else if (statusesMatch && isPollingRef.current) {
      // Statuses match and we're polling - stop polling
      isPollingRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [statusesMatch, startPolling]);

  // Note: We don't update Redux state after polling because:
  // 1. The component uses local state (displayAuditSummary) from refs, not Redux state
  // 2. No other components currently rely on Redux auditSummary state
  // 3. Redux state is populated on initial load for any component that needs it

  // Initial data load when auditId or businessUnitId changes
  useEffect(() => {
    // Only reload if IDs have changed
    if (
      auditId !== lastAuditIdRef.current ||
      businessUnitId !== lastBusinessUnitIdRef.current
    ) {
      loadDataCalledRef.current = false; // Reset the flag for new audit
      setDataLoaded(false); // Reset data loaded flag
      loadData();
    }
  }, [auditId, businessUnitId, loadData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }

      isPollingRef.current = false;
    };
  }, []);

  return {
    displayAuditSummary,
    currentAudit,
    statusesMatch,
    isPolling: isPollingRef.current,
    isLoading: isLoading || auditSummaryApiStatus.isLoading,
    errorMessage: errorMessage || auditSummaryApiStatus.errorMessage,
    refreshData: loadData,
  };
};

export default useAuditSummary;
